import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Almacén de sesión a prueba de WebView (fix del día de lanzamiento,
// 2026-08-03): en Android el localStorage del WebView puede ser purgado por
// el sistema (limpiadores del fabricante, presión de disco) y AHÍ vivía la
// sesión, así que la app instalada pedía login en cada apertura. En nativo la
// sesión (y el code_verifier de PKCE) se guarda en el almacenamiento NATIVO
// (@capacitor/preferences: SharedPreferences, sobrevive siempre) con ESPEJO
// en localStorage: el arranque offline (leerSesionPersistida en startup.js)
// sigue leyendo el espejo síncrono, y la primera apertura tras esta versión
// MIGRA la sesión que hubiera quedado solo en localStorage (nadie se
// desloguea con el update). supabase-js v2 acepta storage asíncrono.
const storageNativo = {
  async getItem(key) {
    try {
      const { value } = await Preferences.get({ key });
      if (value != null) return value;
    } catch { /* plugin ausente o fallo nativo: cae al espejo */ }
    try {
      const local = localStorage.getItem(key);
      if (local != null) {
        try { await Preferences.set({ key, value: local }); } catch { /* noop */ }
        return local;
      }
    } catch { /* localStorage no disponible */ }
    return null;
  },
  async setItem(key, value) {
    try { await Preferences.set({ key, value }); } catch { /* noop */ }
    try { localStorage.setItem(key, value); } catch { /* noop */ }
  },
  async removeItem(key) {
    try { await Preferences.remove({ key }); } catch { /* noop */ }
    try { localStorage.removeItem(key); } catch { /* noop */ }
  },
};

// En el WebView nativo el redirect de OAuth llega como deep link y el code se
// canjea a mano (ver lib/nativeAuth.js), así que usamos PKCE y desactivamos el
// auto-detect de sesión en la URL. En web no pasamos opciones para conservar
// exactamente el comportamiento actual de producción.
const authOptions = Capacitor.isNativePlatform()
  ? { flowType: 'pkce', detectSessionInUrl: false, persistSession: true, autoRefreshToken: true, storage: storageNativo }
  : undefined;

function createTestQueryBuilder(result = { data: [], error: null }) {
  const builder = {
    select: () => builder,
    insert: () => builder,
    update: () => builder,
    delete: () => builder,
    order: () => builder,
    limit: () => builder,
    eq: () => builder,
    single: () => Promise.resolve({ data: null, error: null }),
    maybeSingle: () => Promise.resolve({ data: null, error: null }),
    then: (resolve, reject) => Promise.resolve(result).then(resolve, reject),
  };

  return builder;
}

function createTestSupabaseClient() {
  return {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null } }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signInWithOAuth: () => Promise.resolve({ data: null, error: null }),
      signInWithOtp: () => Promise.resolve({ data: null, error: null }),
      verifyOtp: () => Promise.resolve({ data: { user: null, session: null }, error: null }),
      signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: null }),
      signInWithIdToken: () => Promise.resolve({ data: { user: null, session: null }, error: null }),
      updateUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signOut: () => Promise.resolve({ error: null }),
    },
    from: () => createTestQueryBuilder(),
  };
}

const useTestStub = process.env.NODE_ENV === 'test' && (!supabaseUrl || !supabaseAnonKey);

export const supabase = useTestStub
  ? createTestSupabaseClient()
  : createClient(supabaseUrl, supabaseAnonKey, authOptions ? { auth: authOptions } : undefined);
